import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { endpoints } from '@/api'
import { ApiError } from '@/api/client-exports'
import type { OrganizerSessionResponse } from '@/api'

/**
 * The signed-in organizer, if any (SPEC §14.1).
 *
 * Entirely separate from the household session. The two authentication models
 * do not share state, and one being present says nothing about the other.
 *
 * Nothing here is a security boundary. The session lives in an HttpOnly cookie
 * the browser cannot read, and every protected endpoint checks it for itself.
 * This store only decides what to render.
 */
export const useOrganizerAuthStore = defineStore('organizerAuth', () => {
  const organizerId = ref<string | null>(null)
  const displayName = ref<string | null>(null)
  const email = ref<string | null>(null)

  /** True once the server has been asked. False means "not yet known". */
  const isResolved = ref(false)

  /** Shared between concurrent callers so a cold start asks once. */
  let inFlight: Promise<void> | null = null

  const isSignedIn = computed(() => organizerId.value !== null)

  function adopt(session: OrganizerSessionResponse): void {
    organizerId.value = session.organizerId
    displayName.value = session.displayName
    email.value = session.email
  }

  function clear(): void {
    organizerId.value = null
    displayName.value = null
    email.value = null
  }

  /**
   * Resolves the session once, on a cold start.
   *
   * A 401 is the ordinary answer for a signed-out visitor, not a failure. Any
   * other error leaves `isResolved` false, so the router guard holds rather
   * than redirecting someone to sign in because their connection blipped.
   */
  async function ensureLoaded(): Promise<void> {
    if (isResolved.value) return
    if (inFlight) return inFlight

    inFlight = (async () => {
      try {
        adopt(await endpoints.currentOrganizer())
        isResolved.value = true
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          clear()
          isResolved.value = true
        }
        // Anything else: leave unresolved. Guessing "signed out" on a network
        // error would sign people out of a working session.
      } finally {
        inFlight = null
      }
    })()

    return inFlight
  }

  async function signIn(credentials: { email: string; password: string }): Promise<void> {
    adopt(await endpoints.signInOrganizer(credentials))
    isResolved.value = true
  }

  /**
   * Ends the session.
   *
   * Local state is cleared whatever the server said. If the request failed the
   * cookie may still be live, but leaving the UI showing a signed-in organizer
   * after they pressed sign out is the worse of the two, and the next protected
   * request will settle it.
   */
  async function signOut(): Promise<void> {
    try {
      await endpoints.signOutOrganizer()
    } finally {
      clear()
      isResolved.value = true
    }
  }

  return {
    organizerId,
    displayName,
    email,
    isResolved,
    isSignedIn,
    ensureLoaded,
    signIn,
    signOut,
    clear,
  }
})
